import Transaction from '@/models/Transaction';
import Equipment from '@/models/Equipment';
import Address from '@/models/Address';
import Price from '@/models/Price';

import { EquipmentSource, PaymentModes } from '@/utils/constants'

export function createJsTransaction(state, requestSequenceNumber) {
    let transaction = Transaction.query().with('deliveryPeriod|material|positions.warenempfaenger').first();
    let relevantAddressIds = [];
    let relevantEquipmentIds = [];

    let jsTransaction = {
        ProductType: state.Product,
        RequestSequenceNumber: requestSequenceNumber,
        OfferId: transaction.offerId,
        CampaignParticipantId: transaction.campaignParticipantId,
        PriceResponseId: transaction.priceResponseId,
        AuftraggeberId: transaction.auftraggeber_id,
        ReguliererId: transaction.regulierer_id,
        SelectedDeliveryPeriodId: transaction.deliveryPeriod_id,
        SelectedMaterialId: transaction.material_id,
        
        //WWW-2163 - disabled for now
        //DSEAccepted: transaction.dseAccepted,
        
        WantsNewsletter: transaction.wantsNewsletter,
        MyClimate: transaction.myClimate,
        DiscountCode: transaction.positions.length > 1 ? '' : transaction.discountCode,
        PaymentMode: transaction.paymentMode,
        DifferentPaymentConfigurationText: transaction.paymentMode == PaymentModes.Other ? transaction.differentPaymentConfigurationText : null,
        Positions: transaction.positions.map((pos) => {
            return {
                ID: pos.id,
                Amount: pos.amount,
                WarenempfaengerId: pos.warenempfaenger_id,
                ReguliererId: pos.regulierer_id,
                EquipmentId: pos.equipment_id,
                WantsFillOption: pos.wantsFillOption
            }
        })
    };

    if (transaction.auftraggeber_id && !relevantAddressIds.includes(transaction.auftraggeber_id)) {
        relevantAddressIds.push(transaction.auftraggeber_id);
    }
    transaction.positions.forEach((pos) => {
        if (pos.warenempfaenger_id && !relevantAddressIds.includes(pos.warenempfaenger_id)) {
            relevantAddressIds.push(pos.warenempfaenger_id);
        }
        if (pos.regulierer_id && !relevantAddressIds.includes(pos.regulierer_id)) {
            relevantAddressIds.push(pos.regulierer_id);
        }
        if (pos.equipment_id && !relevantEquipmentIds.includes(pos.equipment_id)) {
            relevantEquipmentIds.push(pos.equipment_id);
        }
    });

    jsTransaction.Addresses = Address.findIn(relevantAddressIds).map((address) => {
        return {
            ID: address.id,
            CustomerID: address.customerId,
            AddressSource: address.addressSource,
            Salutation: address.salutation,
            FirstName: address.firstName,
            LastName: address.lastName,
            CompanyName: address.companyName,
            Street: address.street,
            StreetNumber: address.streetNumber,
            AddressLine2: address.addressLine2,
            Zipcode: address.zipcode,
            City: address.city,
            CountryCode: address.countryCode,
            Phone: address.phone,
            EmailAddress: address.emailAddress,
            CumulusNumber: address.cumulusNumber,
            MLGuid: address.mlGuid
        }
    });

    jsTransaction.Equipments = Equipment.findIn(relevantEquipmentIds).map((equipment) => {
        return {
            ID: equipment.id,
            SapID: equipment.sap_id,
            EquipmentSource: equipment.equipmentSource,
            Quantity: equipment.quantity,
            Size: equipment.size
        }
    });

    return jsTransaction;
}

export function populatePrices(prices) {
    Price.deleteAll();
    if (prices) {
        Price.insert({
            data: prices.map((price) => {
                return {
                    material: price.Material,
                    position_id: price.PositionID,
                    priceType: price.PriceType,
                    price: price.Price
                }
            })
        });
    }
}

export function populateEquipments(equipments) {
    Equipment.deleteAll();
    if (equipments && equipments.length > 0) {
        Equipment.insert({
            data: equipments.map((equipment) => {
                return {
                    sap_id: equipment.SapID,
                    quantity: equipment.Quantity,
                    size: equipment.Size,
                    equipmentSource: EquipmentSource.ServerEquipment
                }
            })
        });
    }
}

export function createAddressFromServerAddress(address) {
    return {
        id: address.ID,
        customerId: address.CustomerID,
        addressSource: address.AddressSource,
        isAuftraggeber: address.IsAuftraggeber,
        isWarenempfaenger: address.IsWarenempfaenger,
        isRegulierer: address.IsRegulierer,
        salutation: address.Salutation,
        firstName: address.FirstName,
        lastName: address.LastName,
        companyName: address.CompanyName,
        street: address.Street,
        streetNumber: address.StreetNumber,
        addressLine2: address.AddressLine2,
        zipcode: address.Zipcode,
        city: address.City,
        countryCode: address.CountryCode,
        phone: address.Phone,
        emailAddress: address.EmailAddress,
        cumulusNumber: address.CumulusNumber,
        mlGuid: address.MLGuid
    }
}

export function clone(source) {
    // using native JSON functions removes reactivity
    // so we can clone an object without mutating the original source
    return JSON.parse(JSON.stringify(source));
}

export function getEnumOptions(enumsArray) {
    let res = [];
    enumsArray.forEach(function (enumVal) {
        res.push({ text: enumVal.text, id: enumVal.value })
    });
    return res;
}

function scrollValidationErrorsIntoView(validationObserver) {
    //get errors from validationObserver
    const errors = Object.entries(validationObserver.refs)
        .map(([key, value]) => ({
            key,
            value
        }))
        .filter(error => {
            if (!error || !error.value || !error.value.failedRules) return false;
            return Object.keys(error.value.failedRules).length > 0;
        });

    //pick first error and scroll to it
    if (errors && errors.length > 0) {
        window.setTimeout(() => {
            validationObserver.refs[errors[0]['key']].$el.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }, 100);
    }
}

export function doValidation(validationObserver, validCallback, invalidCallback) {
    if (validationObserver) {
        validationObserver.validate().then((isValid) => {
            if (isValid) {
                if (validCallback) {
                    validCallback();
                }
            } else {
                scrollValidationErrorsIntoView(validationObserver);
                if (invalidCallback) {
                    invalidCallback();
                }
            }
        });
    } else {
        throw 'validationObserver is missing';
    }
}

export function triggerValidation(validationObserver) {
    //check if a validationObserver was passed
    if (validationObserver === undefined || validationObserver == null) {
        throw 'validationObserver is missing';
    }

    //call validation
    var promise = validationObserver.validate();
    promise.then((isValid) => {
        if (!isValid) {
            //things were not valid, scroll to error
            scrollValidationErrorsIntoView(validationObserver)
        }
    });

    return promise;
}

export function stepBeforeRouteLeave(allowedNavigationPaths, validationObserver, to, next) {

    //try to find the to.path in the list of allowed paths
    var matchingPaths = JSON.parse(JSON.stringify(allowedNavigationPaths)).filter(p => p.path == to.path);
    if (matchingPaths.length == 0) {
        next(false);
        return;
    }

    const allowedPath = matchingPaths[0];

    //see if validation must be triggered
    if (!allowedPath.requiresValidation) {
        //no validation required -> call next
        next();
        return;
    }

    if (triggerValidation(validationObserver).then((isValid) => {
        if (isValid) {
            next(); //validation ok
        } else {
            next(false); //validation had errors
        }
    }));
}